import { lazy } from 'react'
import type { RouteObject } from 'react-router-dom'
import { RequirePermission } from '@/auth/RequirePermission'
import { permissionCode } from '@/lib/permissions'

/**
 * Administration routes.
 *
 * Every entry is gated by an explicit permission code rather than the user's
 * entity: the API re-checks the same code, and an account can gain extra
 * permissions through a group. Keep the gate aligned with the endpoint it
 * protects.
 *
 * Groups share the Users module: the list and read-only detail sit behind
 * `users:view`, while creating and editing require `users:edit`. Admin pages are
 * code-split (loaded from `AppShell`'s suspense boundary) so non-admin users
 * never download them.
 */
const StaffPage = lazy(() => import('@/pages/staff/StaffPage'))
const StaffCreatePage = lazy(() => import('@/pages/staff/StaffCreatePage'))
const ArchivedStaffPage = lazy(() => import('@/pages/staff/ArchivedStaffPage'))
const StaffDetailPage = lazy(() => import('@/pages/staff/StaffDetailPage'))
const GroupsPage = lazy(() => import('@/pages/groups/GroupsPage'))
const GroupDetailPage = lazy(() => import('@/pages/groups/GroupDetailPage'))
const GroupCreatePage = lazy(() => import('@/pages/groups/GroupCreatePage'))
const GroupEditPage = lazy(() => import('@/pages/groups/GroupEditPage'))
const AuditTrailPage = lazy(() => import('@/pages/audit-trail/AuditTrailPage'))
const AuditTrailDetailPage = lazy(() => import('@/pages/audit-trail/AuditTrailDetailPage'))

export const adminRoutes: RouteObject[] = [
  {
    path: 'staff',
    element: (
      <RequirePermission permission={permissionCode('users', 'view')}>
        <StaffPage />
      </RequirePermission>
    ),
  },
  {
    path: 'staff/archived',
    element: (
      <RequirePermission permission={permissionCode('users', 'view')}>
        <ArchivedStaffPage />
      </RequirePermission>
    ),
  },
  {
    path: 'staff/new',
    element: (
      <RequirePermission permission={permissionCode('users', 'create')}>
        <StaffCreatePage />
      </RequirePermission>
    ),
  },
  {
    path: 'staff/:id',
    element: (
      <RequirePermission permission={permissionCode('users', 'view')}>
        <StaffDetailPage />
      </RequirePermission>
    ),
  },
  {
    path: 'groups',
    element: (
      <RequirePermission permission={permissionCode('users', 'view')}>
        <GroupsPage />
      </RequirePermission>
    ),
  },
  {
    path: 'groups/new',
    element: (
      <RequirePermission permission={permissionCode('users', 'edit')}>
        <GroupCreatePage />
      </RequirePermission>
    ),
  },
  {
    path: 'groups/:id',
    element: (
      <RequirePermission permission={permissionCode('users', 'view')}>
        <GroupDetailPage />
      </RequirePermission>
    ),
  },
  {
    path: 'groups/:id/edit',
    element: (
      <RequirePermission permission={permissionCode('users', 'edit')}>
        <GroupEditPage />
      </RequirePermission>
    ),
  },
  // The whole system's trail. Gated on `users:view`, matching the API.
  {
    path: 'audit-trail',
    element: (
      <RequirePermission permission={permissionCode('users', 'view')}>
        <AuditTrailPage />
      </RequirePermission>
    ),
  },
  // One entry of the trail. Deep-linkable so evidence can be shared verbatim.
  {
    path: 'audit-trail/:id',
    element: (
      <RequirePermission permission={permissionCode('users', 'view')}>
        <AuditTrailDetailPage />
      </RequirePermission>
    ),
  },
]
